import React from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GENDER_LABELS, PROGRAM_LABELS } from "@/lib/api";

const PROGRAMS = ["ALL", "AA", "NA", "CMA", "SMART", "MULTIPLE", "OTHER"];

function Field({ label, children, testId }) {
  return (
    <label className="flex flex-col gap-1.5 min-w-0" data-testid={testId}>
      <span className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">{label}</span>
      {children}
    </label>
  );
}

const selectClass =
  "h-10 rounded-xl border border-[#E3DED4] bg-white px-3 text-sm text-[#1F2A2E] focus:outline-none focus:ring-2 focus:ring-[#2A8C97] w-full";

export default function FilterBar({ filters, setFilters, total, loading }) {
  const [expanded, setExpanded] = React.useState(false);
  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value, page: 1 });
  const activeCount = [
    filters.program !== "ALL",
    filters.availability !== "",
    filters.communication !== "",
    filters.gender !== "",
    filters.state !== "",
    filters.country !== "",
    filters.online_only,
  ].filter(Boolean).length;

  return (
    <div className="sm-card p-4 md:p-5 bg-white">
      <div className="flex flex-col md:flex-row gap-3 md:items-end">
        <div className="flex-1">
          <Field label="Search">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A868B]" />
              <Input
                value={filters.q}
                onChange={set("q")}
                data-testid="sponsor-search-input"
                placeholder="Search names or introductions"
                className="pl-9 h-10 bg-white border-[#E3DED4] rounded-xl"
              />
            </div>
          </Field>
        </div>
        <div className="w-full md:w-48">
          <Field label="Recovery Program">
            <select
              value={filters.program}
              onChange={set("program")}
              data-testid="filter-program"
              className={selectClass}
            >
              {PROGRAMS.map((p) => (
                <option key={p} value={p}>
                  {p === "ALL" ? "All programs" : PROGRAM_LABELS[p]}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="w-full md:w-44">
          <Field label="Availability">
            <select
              value={filters.availability}
              onChange={set("availability")}
              data-testid="filter-availability"
              className={selectClass}
            >
              <option value="">Any availability</option>
              <option value="accepting">Accepting sponsees</option>
              <option value="not_accepting">Not currently accepting</option>
            </select>
          </Field>
        </div>
        <Button
          variant="outline"
          onClick={() => setExpanded((v) => !v)}
          data-testid="more-filters-button"
          className="h-10 bg-white border-[#E3DED4] text-[#1F2A2E] hover:bg-[#F2EFE8]"
        >
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />
          More filters
          {activeCount > 0 && (
            <span className="ml-2 px-1.5 rounded-full bg-[#1F6F78] text-white text-xs">
              {activeCount}
            </span>
          )}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-[#E3DED4] grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Communication Preference">
            <select
              value={filters.communication}
              onChange={set("communication")}
              data-testid="filter-communication"
              className={selectClass}
            >
              <option value="">Any</option>
              <option value="messaging">Messaging</option>
              <option value="phone">Phone</option>
              <option value="video">Video</option>
              <option value="in_person">In-person</option>
            </select>
          </Field>
          <Field label="Gender Preference">
            <select
              value={filters.gender}
              onChange={set("gender")}
              data-testid="filter-gender"
              className={selectClass}
            >
              <option value="">No preference</option>
              {["man", "woman", "nonbinary"].map((g) => (
                <option key={g} value={g}>
                  {GENDER_LABELS[g]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="State / Region">
            <Input
              value={filters.state}
              onChange={set("state")}
              data-testid="filter-state"
              placeholder="e.g. TX"
              className="h-10 bg-white border-[#E3DED4] rounded-xl"
            />
          </Field>
          <Field label="Country">
            <Input
              value={filters.country}
              onChange={set("country")}
              data-testid="filter-country"
              placeholder="e.g. USA"
              className="h-10 bg-white border-[#E3DED4] rounded-xl"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[#1F2A2E] sm:col-span-2">
            <input
              type="checkbox"
              checked={filters.online_only}
              onChange={(e) => setFilters({ ...filters, online_only: e.target.checked, page: 1 })}
              data-testid="filter-online-only"
              className="w-4 h-4 accent-[#1F6F78]"
            />
            Online now only
          </label>
          <div className="sm:col-span-2 flex items-center justify-end">
            <Button
              variant="ghost"
              onClick={() =>
                setFilters({
                  q: "",
                  program: "ALL",
                  availability: "",
                  communication: "",
                  gender: "",
                  state: "",
                  country: "",
                  online_only: false,
                  page: 1,
                })
              }
              data-testid="clear-filters-button"
              className="text-[#B42318] hover:bg-[#FBE6E4]"
            >
              <X className="w-4 h-4 mr-1" /> Clear all filters
            </Button>
          </div>
        </div>
      )}

      <p data-testid="sponsor-result-count" className="mt-3 text-sm text-[#4B5A60]">
        {loading ? "Loading sponsors..." : `${total} ${total === 1 ? "sponsor" : "sponsors"} found`}
        <span className="ml-1 text-[#1F6F78] font-semibold">Browsing is always free.</span>
      </p>
    </div>
  );
}
